// React / Next
import { motion, Variants } from 'framer-motion';

// Components
import { ALogoIcon } from '../../assets/icons/ALogoIcon';
import { ExcelIcon } from '../../assets/icons/ExcelIcon';
import { TeamsIcon } from '../../assets/icons/TeamsIcon';
import { ArchieChatInput } from './ArchieChatInput';

// Types
import { CapabilityCardType } from './types';

// ==========================================
// ANIMATION VARIANTS
// ==========================================
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.1,
    },
  },
};

const itemVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 16,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

// ==========================================
// ICON COMPONENTS
// ==========================================
const XlsxIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 10 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.25 1.25H2.5C2.16848 1.25 1.85054 1.3817 1.61612 1.61612C1.3817 1.85054 1.25 2.16848 1.25 2.5V7.5C1.25 7.83152 1.3817 8.14946 1.61612 8.38388C1.85054 8.6183 2.16848 8.75 2.5 8.75H7.5C7.83152 8.75 8.14946 8.6183 8.38388 8.38388C8.6183 8.14946 8.75 7.83152 8.75 7.5V3.75L6.25 1.25Z"
      stroke="currentColor"
      strokeWidth="0.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.25 1.25V3.75H8.75"
      stroke="currentColor"
      strokeWidth="0.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.125 5.625L4.375 6.875M4.375 5.625L3.125 6.875"
      stroke="currentColor"
      strokeWidth="0.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.625 6.875L6.25 5.625L6.875 6.875"
      stroke="currentColor"
      strokeWidth="0.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ==========================================
// SECTION HEADING COMPONENT - Enforces Signifier font
// Scaled: 14px/19.5px (from 20px/28px * 0.697)
// ==========================================
type SectionHeadingProps = {
  children: React.ReactNode;
};

const SectionHeading = ({ children }: SectionHeadingProps) => (
  <p
    className="text-[14px] leading-[19.5px] text-blue-1000"
    style={{
      fontFamily: "'Signifier', serif",
      fontWeight: 200,
    }}
  >
    {children}
  </p>
);

// ==========================================
// EXCEL ANALYSIS CARD - Scaled to match Figma proportions
// Scale factor: ~0.697 (11.154/16)
// ==========================================
const ExcelAnalysisCard = () => (
  <motion.article
    // Card: p-11px, rounded-8px (scaled from p-16, rounded-12)
    className="bg-gray-50 rounded-[8px] p-[11px] w-full max-w-[488px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.04),0px_10px_12px_0px_rgba(0,0,0,0.08)]"
    aria-label="Excel analysis showcase card"
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    {/* Chat Container - gap-11px (scaled from 16px) */}
    <div className="flex flex-col gap-[11px]">
      {/* User Message Bubble */}
      <motion.div className="flex flex-col" variants={itemVariants}>
        {/* Bubble: p-11px, rounded-11px (scaled from p-16, rounded-16) */}
        <div className="bg-white border border-[#e5e7f0] rounded-[11px] p-[11px] w-full shadow-[0px_1.4px_2.8px_-1.4px_rgba(0,0,0,0.08),0px_2.8px_5.6px_-1.4px_rgba(0,0,0,0.04)]">
          {/* Text container - gap-5.5px, py-2.8px (scaled from gap-8, py-4) */}
          <div className="flex flex-col gap-[5.5px] py-[2.8px]">
            {/* Message text - 11px/17px (scaled from 16px/24px) */}
            <p
              className="text-[11px] leading-[17px] text-blue-1000"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
            >
              Hey, Archie. Can you analyze the structure of this file?
            </p>
            {/* File attachment - scaled padding and gap */}
            <div className="flex items-center gap-[2.8px] bg-gray-50 border border-gray-200 rounded-[3.5px] pl-[2.8px] pr-[5.5px] py-[2.8px] w-fit">
              <div className="bg-[rgba(2,115,60,0.08)] rounded-[1.4px] p-[4.2px] flex items-center justify-center">
                <XlsxIcon className="w-[7px] h-[7px] text-[#02733c]" />
              </div>
              {/* File label - 8.4px/11px (scaled from 12px/16px) */}
              <span
                className="text-[8.4px] leading-[11px] text-[#595f7a]"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                Trial_Balance_Oct2025.xlsx
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Assistant Response Container */}
      <div className="flex flex-col">
        {/* Intro text bubble - p-5.5px (scaled from p-8) */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[5.5px] rounded-[11px]">
            <div className="flex flex-col py-[2.8px]">
              {/* Body text - 11px/17px */}
              <p
                className="text-[11px] leading-[17px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                Spreadsheet analyzed. Here's what I found:
              </p>
            </div>
          </div>
        </motion.div>

        {/* Table Structure Section - p-5.5px, gap-5.5px */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[5.5px] rounded-[11px] flex flex-col gap-[5.5px]">
            <div className="flex flex-col py-[2.8px]">
              <SectionHeading>Table Structure</SectionHeading>
            </div>
            <div className="flex flex-col py-[2.8px]">
              {/* List - 11px/17px */}
              <ul
                className="list-disc pl-[14px] text-[11px] leading-[17px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <li>
                  <span style={{ fontWeight: 600 }}>Multi-row header</span>
                  <span style={{ fontWeight: 400 }}> detected (rows 1-2)</span>
                </li>
                <li>
                  <span style={{ fontWeight: 600 }}>Data range:</span>
                  <span style={{ fontWeight: 400 }}> A3:F247 (244 records)</span>
                </li>
                <li>
                  <span style={{ fontWeight: 400 }}>
                    Hierarchical account structure identified
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Column Analysis Section */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[5.5px] rounded-[11px] flex flex-col gap-[5.5px]">
            <div className="flex flex-col py-[2.8px]">
              <SectionHeading>Column Analysis (6 columns)</SectionHeading>
            </div>
            <div className="flex flex-col py-[2.8px]">
              <ul
                className="list-disc pl-[14px] text-[11px] leading-[17px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <li>
                  <span style={{ fontWeight: 600 }}>A:</span>
                  <span style={{ fontWeight: 400 }}> Account Code</span>
                </li>
                <li>
                  <span style={{ fontWeight: 600 }}>B:</span>
                  <span style={{ fontWeight: 400 }}> Account Name</span>
                </li>
                <li>
                  <span style={{ fontWeight: 600 }}>C:</span>
                  <span style={{ fontWeight: 400 }}> Debit (Currency)</span>
                </li>
                <li>
                  <span style={{ fontWeight: 600 }}>D:</span>
                  <span style={{ fontWeight: 400 }}> Credit (Currency)</span>
                </li>
                <li>
                  <span style={{ fontWeight: 600 }}>E:</span>
                  <span style={{ fontWeight: 400 }}> Period (Date)</span>
                </li>
                <li>
                  <span style={{ fontWeight: 600 }}>F:</span>
                  <span style={{ fontWeight: 400 }}> Category (Text)</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Pattern Recognition Section */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[5.5px] rounded-[11px] flex flex-col gap-[5.5px]">
            <div className="flex flex-col py-[2.8px]">
              <SectionHeading>Pattern Recognition</SectionHeading>
            </div>
            <div className="flex flex-col py-[2.8px]">
              <ul
                className="list-disc pl-[14px] text-[11px] leading-[17px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <li>
                  <span style={{ fontWeight: 600 }}>Document type:</span>
                  <span style={{ fontWeight: 400 }}> Trial Balance</span>
                </li>
                <li>
                  <span style={{ fontWeight: 600 }}>GL format:</span>
                  <span style={{ fontWeight: 400 }}> Standard Chart of Accounts</span>
                </li>
                <li>
                  <span style={{ fontWeight: 600 }}>Data quality:</span>
                  <span style={{ fontWeight: 400 }}>
                    {' '}
                    98% clean (5 minor formatting issues)
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </motion.article>
);

// ==========================================
// EXCEL SIDEBAR CARD - Icons
// ==========================================
const TableIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="1" y="1" width="10" height="10" rx="1" stroke="currentColor" strokeWidth="0.8" />
    <line x1="1" y1="4" x2="11" y2="4" stroke="currentColor" strokeWidth="0.8" />
    <line x1="4" y1="1" x2="4" y2="11" stroke="currentColor" strokeWidth="0.8" />
    <line x1="7.5" y1="1" x2="7.5" y2="11" stroke="currentColor" strokeWidth="0.8" />
  </svg>
);

const FileIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7 1H3C2.44772 1 2 1.44772 2 2V10C2 10.5523 2.44772 11 3 11H9C9.55228 11 10 10.5523 10 10V4L7 1Z"
      stroke="currentColor"
      strokeWidth="0.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M7 1V4H10" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M4.5 3L7.5 6L4.5 9" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ==========================================
// EXCEL SIDEBAR CARD - Spreadsheet Table
// Scale factor: ~0.663 (matching design proportions)
// ==========================================
const SPREADSHEET_DATA = [
  { date: '2026-01-01', journalId: 'JE-10231', accountCode: '1100', accountName: 'Cash' },
  { date: '2026-01-02', journalId: 'JE-10232', accountCode: '4000', accountName: 'Revenue' },
  { date: '2026-01-05', journalId: 'JE-10233', accountCode: '6100', accountName: 'Office Expen' },
  { date: '2026-01-05', journalId: 'JE-10234', accountCode: '1100', accountName: 'Depreciation' },
  { date: '2026-01-10', journalId: 'JE-10235', accountCode: '6200', accountName: 'Accumulated' },
  { date: '2026-01-10', journalId: 'JE-10236', accountCode: '1500', accountName: 'Cash' },
  { date: '2026-01-15', journalId: 'JE-10237', accountCode: '6000', accountName: 'Cash' },
  { date: '2026-01-15', journalId: 'JE-10238', accountCode: '2000', accountName: 'Cash' },
  { date: '2026-01-15', journalId: 'JE-10239', accountCode: '1100', accountName: 'Cash' },
];

const SpreadsheetTable = () => (
  <div className="relative">
    {/* Table Grid */}
    <div className="grid grid-cols-4">
      {/* Header Row */}
      <div className="bg-white border-l border-t border-[#d0d0d0] h-[22px] flex items-center justify-center">
        <span
          className="text-[6.6px] leading-[13.3px] text-blue-1000 font-semibold"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Date
        </span>
      </div>
      <div className="bg-white border-l border-t border-[#d0d0d0] h-[22px] flex items-center justify-center">
        <span
          className="text-[6.6px] leading-[13.3px] text-blue-1000 font-semibold"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Journal ID
        </span>
      </div>
      <div className="bg-white border-l border-t border-[#d0d0d0] h-[22px] flex items-center justify-center">
        <span
          className="text-[6.6px] leading-[13.3px] text-blue-1000 font-semibold"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Account Code
        </span>
      </div>
      <div className="bg-white border-l border-t border-r border-[#d0d0d0] h-[22px] flex items-center justify-center">
        <span
          className="text-[6.6px] leading-[13.3px] text-blue-1000 font-semibold"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Account Name
        </span>
      </div>

      {/* Data Rows */}
      {SPREADSHEET_DATA.map((row, index) => (
        <div key={index} className="contents">
          <div className={`bg-white border-l border-t ${index === SPREADSHEET_DATA.length - 1 ? 'border-b' : ''} border-[#d0d0d0] h-[22px] flex items-center justify-center`}>
            <span
              className="text-[6.6px] leading-[13.3px] text-blue-1000"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
            >
              {row.date}
            </span>
          </div>
          <div className={`bg-white border-l border-t ${index === SPREADSHEET_DATA.length - 1 ? 'border-b' : ''} border-[#d0d0d0] h-[22px] flex items-center justify-center`}>
            <span
              className="text-[6.6px] leading-[13.3px] text-blue-1000"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
            >
              {row.journalId}
            </span>
          </div>
          <div className={`bg-white border-l border-t ${index === SPREADSHEET_DATA.length - 1 ? 'border-b' : ''} border-[#d0d0d0] h-[22px] flex items-center justify-center`}>
            <span
              className="text-[6.6px] leading-[13.3px] text-blue-1000"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
            >
              {row.accountCode}
            </span>
          </div>
          <div className={`bg-white border-l border-t border-r ${index === SPREADSHEET_DATA.length - 1 ? 'border-b' : ''} border-[#d0d0d0] h-[22px] flex items-center justify-center`}>
            <span
              className="text-[6.6px] leading-[13.3px] text-blue-1000"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
            >
              {row.accountName}
            </span>
          </div>
        </div>
      ))}
    </div>

    {/* Selection Border */}
    <div className="absolute inset-0 border-[1.3px] border-blue-800 pointer-events-none" />
  </div>
);

// ==========================================
// EXCEL SIDEBAR CARD - Variance Card Component
// ==========================================
type VarianceCardProps = {
  title: string;
  cellRef: string;
  amount: string;
  description: string;
};

const VarianceCard = ({ title, cellRef, amount, description }: VarianceCardProps) => (
  <motion.div
    className="bg-gray-100 border border-gray-200 rounded-[8px] p-[5.3px]"
    variants={itemVariants}
  >
    <div className="flex flex-col p-[5.3px]">
      <div className="flex flex-col gap-[5.4px]">
        <p
          className="text-[7.95px] leading-[10.6px] text-blue-1000 font-semibold"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {title} ({cellRef})
        </p>
        <p
          className="text-[7.95px] leading-[10.6px] text-blue-1000"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
        >
          {amount} · {description}
        </p>
      </div>
    </div>
  </motion.div>
);

// ==========================================
// EXCEL SIDEBAR CARD - Sidebar Panel
// ==========================================
const SidebarPanel = () => (
  <motion.div
    className="bg-white border border-[#d0d0d0] rounded-[8px] flex flex-col w-[340px] overflow-hidden shadow-[0px_4px_4px_0px_rgba(0,0,0,0.04),0px_10px_12px_0px_rgba(0,0,0,0.08)]"
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    {/* Data Selection Header */}
    <motion.div
      className="bg-white border-b border-gray-200 px-[10.9px] py-[14.5px] flex flex-col gap-[14.5px]"
      variants={itemVariants}
    >
      {/* Top Row - Data Selected + Range */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[7.3px]">
          <TableIcon className="w-[11.8px] h-[11.8px] text-blue-1000" />
          <span
            className="text-[10.9px] leading-[14.5px] text-blue-1000"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
          >
            Data Selected
          </span>
        </div>
        <div className="flex items-center gap-[7.3px]">
          <span
            className="text-[10.9px] leading-[14.5px] text-blue-800"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
          >
            A1:D10
          </span>
          <ChevronRightIcon className="w-[11.8px] h-[11.8px] text-blue-800" />
        </div>
      </div>

      {/* Bottom Row - Sheet Info + Change */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[7.3px]">
          <FileIcon className="w-[11.8px] h-[11.8px] text-gray-600" />
          <span
            className="text-[10.9px] leading-[14.5px] text-gray-600"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
          >
            Sheet 1, A1:D10
          </span>
        </div>
        <span
          className="text-[10.9px] leading-[14.5px] text-blue-800"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
        >
          Change
        </span>
      </div>
    </motion.div>

    {/* Chat Content Area */}
    <motion.div className="flex-1 flex flex-col px-[10.6px] py-[10.6px] gap-[8px]" variants={itemVariants}>
      {/* User Query Bubble */}
      <motion.div
        className="bg-white border border-gray-200 rounded-[10.6px] p-[10.6px] shadow-[0px_1.3px_2.7px_-2px_rgba(0,0,0,0.08),0px_2.7px_5.3px_-2px_rgba(0,0,0,0.04)]"
        variants={itemVariants}
      >
        <p
          className="text-[10.6px] leading-[15.9px] text-blue-1000"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
        >
          "Why did expenses spike 40% in October?"
        </p>
      </motion.div>

      {/* AI Response Text */}
      <motion.div className="px-[5.3px] py-[2.7px]" variants={itemVariants}>
        <p
          className="text-[10.6px] leading-[15.9px] text-blue-1000"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
        >
          I've identified the variance drivers:
        </p>
      </motion.div>

      {/* Variance Cards */}
      <motion.div className="flex flex-col gap-[8px]" variants={itemVariants}>
        <VarianceCard
          title="Legal Services"
          cellRef="D23"
          amount="+$42,300"
          description="One-time litigation expense"
        />
        <VarianceCard
          title="Marketing"
          cellRef="D31-D34"
          amount="+$18,700"
          description="Q4 campaign launch"
        />
        <VarianceCard
          title="IT Services"
          cellRef="D41"
          amount="+$3,200"
          description="Software renewal"
        />
      </motion.div>

      {/* Anomalies Footer */}
      <motion.div className="px-[5.3px] py-[2.7px]" variants={itemVariants}>
        <p
          className="text-[10.6px] leading-[15.9px] text-blue-1000"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
        >
          2 anomalies detected in range · Source cells cited
        </p>
      </motion.div>
    </motion.div>

    {/* Suggestions Section */}
    <motion.div className="px-[10.6px] pb-[8px] flex flex-col gap-[8.8px]" variants={itemVariants}>
      <p
        className="text-[7.4px] leading-normal text-gray-600 uppercase font-semibold"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        Suggestions
      </p>
      <div className="flex flex-wrap gap-[5.9px]">
        <div className="bg-gray-50 border border-gray-200 rounded-[2.9px] px-[8.8px] py-[1.5px] flex items-center">
          <span
            className="text-[8.8px] leading-none text-blue-1000"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
          >
            Create journal entry from this analysis
          </span>
        </div>
      </div>
    </motion.div>

    {/* Chat Input */}
    <motion.div className="flex justify-center px-[10px] pb-[14px]" variants={itemVariants}>
      <ArchieChatInput
        size="lg"
        placeholder="Ready to talk spreadsheets..."
        className="w-full"
      />
    </motion.div>
  </motion.div>
);

// ==========================================
// EXCEL SIDEBAR CARD - Main Component
// ==========================================
const ExcelSidebarCard = () => (
  <motion.article
    className="relative w-full max-w-[488px] h-[505px]"
    aria-label="Excel sidebar showcase card"
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    {/* Center - Sidebar Panel (animates first, overlapping) */}
    <motion.div
      className="absolute z-10 left-1/2 -translate-x-1/2 top-0"
      variants={itemVariants}
    >
      <SidebarPanel />
    </motion.div>

    {/* Left - Spreadsheet Table (animates second with delay, slides from left) */}
    <motion.div
      className="absolute z-0 left-0 top-[96px]"
      initial={{ opacity: 0, x: -40 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        delay: 0.5,
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
    >
      <SpreadsheetTable />
    </motion.div>
  </motion.article>
);

// ==========================================
// MICROSOFT INTEGRATION CARD - Data Constants
// ==========================================
const TRANSCRIPT_DATA = [
  { initials: 'JD', name: 'John', time: '10:32', text: '...the Q3 revenue recognition for the software contract...' },
  { initials: 'SM', name: 'Sarah', time: '10:32', text: 'Under ASC 606 we need to identify the performance obligations...' },
];

const ACTION_ITEMS = [
  { text: 'Review Q3 accrual schedule', checked: false },
  { text: 'Send updated trial balance', checked: false },
  { text: 'Schedule follow-up call', checked: true },
];

const EXCEL_SUGGESTIONS = [
  'Summarize data',
  'Find unpaid invoices',
  'Calculate total spend',
  'Find overdue invoices',
  'Show a summary of vendors',
  'Break down expenses by category',
];

const ASSISTANT_MODES = ['General', 'Tax', 'Audit', 'GAAP'];

// ==========================================
// MICROSOFT INTEGRATION CARD - Teams Sidebar
// Figma node 1065-2137 - Full size: 341px panel, Scale: ~58.6%
// ==========================================

// Recording indicator icon (red dot)
const TeamsRecordingIcon = ({ className }: { className?: string }) => (
  <div className={`bg-[#d13438] rounded-full ${className}`} />
);

// Check icon for checkboxes
const TeamsCheckIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Clock icon for meeting duration
const TeamsClockIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1" />
    <path d="M6 3V6L8 7" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// People/users icon for company
const TeamsPeopleIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="4" cy="3.5" r="1.5" stroke="currentColor" strokeWidth="0.8" />
    <path d="M1 9C1 7.34315 2.34315 6 4 6C5.65685 6 7 7.34315 7 9" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" />
    <circle cx="8" cy="3.5" r="1.5" stroke="currentColor" strokeWidth="0.8" />
    <path d="M11 9C11 7.34315 9.65685 6 8 6C7.5 6 7.03 6.12 6.62 6.33" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" />
  </svg>
);

// Calendar icon for next meeting
const TeamsCalendarIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="2" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="0.8" />
    <path d="M1 5H11" stroke="currentColor" strokeWidth="0.8" />
    <path d="M3.5 1V3" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" />
    <path d="M8.5 1V3" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" />
  </svg>
);

// Mail/envelope icon for draft email
const TeamsMailIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="3" width="12" height="10" rx="1" stroke="currentColor" strokeWidth="1.2" />
    <path d="M2 5L8 9L14 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Download icon for export notes
const TeamsDownloadIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 2V10M8 10L5 7M8 10L11 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 13H13" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

// Share icon for share via teams
const TeamsShareIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 2V10M8 2L5 5M8 2L11 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 9V13H13V9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Checkmark circle icon for meeting ended
const TeamsCheckCircleIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="0.8" />
    <path d="M3.5 6L5.5 8L8.5 4" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Sparkles/AI icon for AI Generated
const TeamsSparklesIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 1V3M4 3V5M4 3H2M4 3H6" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" />
    <path d="M8 6V9M8 9V12M8 9H5M8 9H11" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" />
    <path d="M9 1V2M9 2V3M9 2H8M9 2H10" stroke="currentColor" strokeWidth="0.7" strokeLinecap="round" />
  </svg>
);

// Chevron down icon for "Read more" dropdown
const TeamsChevronDownIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Teams Avatar component
type TeamsAvatarProps = {
  initials: string;
  size?: number;
};

const TeamsAvatar = ({ initials, size = 23 }: TeamsAvatarProps) => (
  <div
    className="bg-[#0f6cbd] flex items-center justify-center flex-shrink-0"
    style={{
      width: size,
      height: size,
      borderRadius: 5,
    }}
  >
    <span
      className="text-white text-center"
      style={{
        fontFamily: "'Segoe UI', sans-serif",
        fontWeight: 600,
        fontSize: size * 0.35,
        lineHeight: size + 'px',
      }}
    >
      {initials}
    </span>
  </div>
);

// Teams Transcript Item component
type TeamsTranscriptItemProps = {
  initials: string;
  name: string;
  time: string;
  text: string;
};

const TeamsTranscriptItem = ({ initials, name, time, text }: TeamsTranscriptItemProps) => (
  <motion.div className="flex" style={{ gap: 8 }} variants={itemVariants}>
    <TeamsAvatar initials={initials} size={23} />
    <div className="flex flex-col flex-1 min-w-0" style={{ gap: 2 }}>
      <div className="flex items-center" style={{ gap: 5 }}>
        <span
          className="text-blue-1000"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400,
            fontSize: 8,
            lineHeight: '12px',
          }}
        >
          {name}
        </span>
        <span
          className="text-gray-600"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400,
            fontSize: 7,
            lineHeight: '9px',
          }}
        >
          {time}
        </span>
      </div>
      <p
        className="text-gray-600"
        style={{
          fontFamily: "'Segoe UI', sans-serif",
          fontWeight: 400,
          fontSize: 7,
          lineHeight: '11px',
        }}
      >
        {text}
      </p>
    </div>
  </motion.div>
);

// Teams Action Item Checkbox component
type TeamsActionItemProps = {
  text: string;
  checked: boolean;
};

const TeamsActionItem = ({ text, checked }: TeamsActionItemProps) => (
  <motion.div className="flex items-center" style={{ gap: 4, paddingTop: 2, paddingBottom: 2 }} variants={itemVariants}>
    <div className="flex items-center" style={{ padding: 1 }}>
      <div
        className={`flex items-center justify-center ${
          checked ? 'bg-blue-800' : 'bg-white border border-gray-600'
        }`}
        style={{
          width: 7,
          height: 7,
          borderRadius: 1,
        }}
      >
        {checked && <TeamsCheckIcon className="text-white" style={{ width: 5, height: 5 }} />}
      </div>
    </div>
    <span
      className="text-gray-600"
      style={{
        fontFamily: "'Inter', sans-serif",
        fontWeight: 400,
        fontSize: 7,
        lineHeight: '9px',
      }}
    >
      {text}
    </span>
  </motion.div>
);

// Teams Assistant Mode Tab component
type TeamsAssistantModeTabProps = {
  label: string;
  isActive: boolean;
};

const TeamsAssistantModeTab = ({ label, isActive }: TeamsAssistantModeTabProps) => (
  <div
    className={`flex items-center ${
      isActive
        ? 'bg-[rgba(21,177,205,0.08)] border border-blue-800'
        : 'bg-gray-100 border border-gray-200'
    }`}
    style={{
      paddingLeft: 7,
      paddingRight: 7,
      paddingTop: 2,
      paddingBottom: 2,
      borderRadius: 2,
    }}
  >
    <span
      className={isActive ? 'text-blue-800' : 'text-gray-600'}
      style={{
        fontFamily: "'Inter', sans-serif",
        fontWeight: 400,
        fontSize: 7,
        lineHeight: '9px',
      }}
    >
      {label}
    </span>
  </div>
);

// Main Teams Sidebar component - scaled to ~58.6% of Figma dimensions
const TeamsSidebarMini = () => (
  <motion.div
    className="relative"
    style={{ width: 250, height: 474 }}
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    {/* Purple left accent strip */}
    <div
      className="absolute bg-[#b5b9e9]"
      style={{
        left: 0,
        top: 0,
        width: 70,
        height: '100%',
        borderTopLeftRadius: 7,
        borderBottomLeftRadius: 7,
      }}
    />

    {/* Teams logo positioned in the purple strip */}
    <motion.div
      className="absolute"
      style={{ left: 14, top: 14, width: 25, height: 23 }}
      variants={itemVariants}
    >
      <TeamsIcon size={25} className="w-full h-full" />
    </motion.div>

    {/* White main panel */}
    <div
      className="absolute bg-white border-2 border-[#b5b9e9] flex flex-col"
      style={{
        left: 50,
        top: 0,
        width: 200,
        height: '100%',
        borderRadius: 7,
      }}
    >
      {/* Header Section - Connected badge + Meeting info */}
      <motion.div
        className="flex flex-col border-b border-gray-200"
        style={{ paddingLeft: 7, paddingRight: 7, paddingTop: 9, paddingBottom: 9, gap: 9 }}
        variants={itemVariants}
      >
        {/* Connected Badge */}
        <div
          className="bg-[rgba(113,212,129,0.08)] self-start flex items-center"
          style={{
            paddingLeft: 7.031,
            paddingRight: 7.031,
            paddingTop: 2.344,
            paddingBottom: 2.344,
            borderRadius: 2.344,
            border: '0.586px solid #71d481',
          }}
        >
          <span
            className="text-[#71d481]"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 400,
              fontSize: 7.031,
              lineHeight: '9.375px',
            }}
          >
            Connected
          </span>
        </div>

        {/* Meeting Title */}
        <span
          className="text-blue-1000"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400,
            fontSize: 9,
            lineHeight: '14px',
          }}
        >
          Client Review - Q3 Close
        </span>

        {/* Recording Status */}
        <div className="flex items-center justify-between">
          <span
            className="text-gray-600"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 400,
              fontSize: 7,
              lineHeight: '9px',
            }}
          >
            Recording: ON
          </span>
          <TeamsRecordingIcon className="w-[8px] h-[8px]" />
        </div>
      </motion.div>

      {/* Assistant Mode Section */}
      <motion.div
        className="flex flex-col border-b border-gray-200"
        style={{ paddingLeft: 7, paddingRight: 7, paddingTop: 9, paddingBottom: 9, gap: 9 }}
        variants={itemVariants}
      >
        <span
          className="text-gray-600"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400,
            fontSize: 7,
            lineHeight: '9px',
          }}
        >
          Assistant Mode
        </span>
        <div className="flex flex-wrap" style={{ gap: 9 }}>
          {ASSISTANT_MODES.map((mode, index) => (
            <TeamsAssistantModeTab key={mode} label={mode} isActive={index === 0} />
          ))}
        </div>
      </motion.div>

      {/* Live Transcript Section */}
      <motion.div
        className="flex flex-col border-b border-gray-200"
        style={{ paddingLeft: 7, paddingRight: 7, paddingTop: 9, paddingBottom: 9, gap: 9 }}
        variants={itemVariants}
      >
        <span
          className="text-gray-600"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400,
            fontSize: 7,
            lineHeight: '9px',
          }}
        >
          Live Transcript
        </span>
        <div className="flex flex-col" style={{ gap: 10 }}>
          {TRANSCRIPT_DATA.map((item, index) => (
            <TeamsTranscriptItem key={index} {...item} />
          ))}
        </div>
        <span
          className="text-blue-800 cursor-pointer"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400,
            fontSize: 7,
            lineHeight: '9px',
          }}
        >
          See full transcript
        </span>
      </motion.div>

      {/* Action Items Section */}
      <motion.div
        className="flex flex-col"
        style={{ paddingLeft: 7, paddingRight: 7, paddingTop: 9, paddingBottom: 9, gap: 9 }}
        variants={itemVariants}
      >
        <span
          className="text-gray-600"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400,
            fontSize: 7,
            lineHeight: '9px',
          }}
        >
          Action Items (3)
        </span>
        <div className="flex flex-col" style={{ gap: 5 }}>
          {ACTION_ITEMS.map((item, index) => (
            <TeamsActionItem key={index} {...item} />
          ))}
        </div>
        <span
          className="text-blue-800 cursor-pointer"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400,
            fontSize: 7,
            lineHeight: '9px',
          }}
        >
          See all action items
        </span>
      </motion.div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Chat Input Section */}
      <div style={{ paddingLeft: 7, paddingRight: 7, paddingTop: 9, paddingBottom: 9 }}>
        <ArchieChatInput size="md" placeholder="Ask Archie" />
      </div>
    </div>
  </motion.div>
);

// ==========================================
// TEAMS POST-MEETING SIDEBAR
// Shows meeting summary after meeting ends
// Scale factor: ~58.6% to match TeamsSidebarMini
// ==========================================

// Data constants for post-meeting sidebar
const POST_MEETING_ACTION_ITEMS = [
  { text: 'Review Q3 accrual schedule', checked: false },
  { text: 'Send updated trial balance', checked: false },
  { text: 'Schedule follow-up call', checked: true },
];

const TeamsPostMeetingSidebar = () => (
  <motion.div
    className="relative"
    style={{ width: 200, height: 474 }}
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    {/* White main panel with purple border - no accent strip */}
    <div
      className="bg-white border-2 border-[#b5b9e9] flex flex-col"
      style={{
        width: 200,
        height: '100%',
        borderRadius: 7,
      }}
    >
      {/* Meeting Ended Header Section */}
      <motion.div
        className="flex flex-col border-b border-gray-200"
        style={{ paddingLeft: 7, paddingRight: 7, paddingTop: 9, paddingBottom: 9, gap: 9 }}
        variants={itemVariants}
      >
        {/* Meeting Ended Badge */}
        <div
          className="bg-[rgba(113,212,129,0.08)] self-start flex items-center"
          style={{
            paddingLeft: 4.688,
            paddingRight: 7.031,
            paddingTop: 2.344,
            paddingBottom: 2.344,
            borderRadius: 2.344,
            border: '0.586px solid #71d481',
            gap: 4.688,
          }}
        >
          <TeamsCheckCircleIcon className="text-[#71d481]" style={{ width: 7, height: 7 }} />
          <span
            className="text-[#71d481]"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 400,
              fontSize: 7.031,
              lineHeight: '9.375px',
            }}
          >
            Meeting Ended
          </span>
        </div>

        {/* Duration Row */}
        <div className="flex items-center" style={{ gap: 5 }}>
          <TeamsClockIcon className="text-gray-600" style={{ width: 10, height: 10 }} />
          <span
            className="text-gray-600"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 400,
              fontSize: 7,
              lineHeight: '9px',
            }}
          >
            1h 23m
          </span>
        </div>

        {/* Company Row */}
        <div className="flex items-center" style={{ gap: 5 }}>
          <TeamsPeopleIcon className="text-gray-600" style={{ width: 10, height: 10 }} />
          <span
            className="text-gray-600"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 400,
              fontSize: 7,
              lineHeight: '9px',
            }}
          >
            Acme Corporation
          </span>
        </div>
      </motion.div>

      {/* Executive Summary Section */}
      <motion.div
        className="flex flex-col border-b border-gray-200"
        style={{ paddingLeft: 7, paddingRight: 7, paddingTop: 9, paddingBottom: 9, gap: 9 }}
        variants={itemVariants}
      >
        <span
          className="text-gray-600"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400,
            fontSize: 7,
            lineHeight: '9px',
          }}
        >
          Executive Summary
        </span>
        <p
          className="text-gray-600"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400,
            fontSize: 9,
            lineHeight: '13px',
          }}
        >
          Reviewed Q1 2026 budget projections with focus on operational efficiency improvements. Client approved 15% increase in t...
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center" style={{ gap: 5 }}>
            <TeamsSparklesIcon className="text-gray-600" style={{ width: 10, height: 10 }} />
            <span
              className="text-gray-600"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 400,
                fontSize: 7,
                lineHeight: '9px',
              }}
            >
              AI Generated
            </span>
          </div>
          <div className="flex items-center" style={{ gap: 5 }}>
            <span
              className="text-blue-800"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 400,
                fontSize: 7,
                lineHeight: '9px',
              }}
            >
              Read more
            </span>
            <TeamsChevronDownIcon className="text-blue-800" style={{ width: 10, height: 10 }} />
          </div>
        </div>
      </motion.div>

      {/* Final Action Items Section */}
      <motion.div
        className="flex flex-col border-b border-gray-200"
        style={{ paddingLeft: 7, paddingRight: 7, paddingTop: 9, paddingBottom: 9, gap: 9 }}
        variants={itemVariants}
      >
        <span
          className="text-gray-600"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400,
            fontSize: 7,
            lineHeight: '9px',
          }}
        >
          Final Action Items
        </span>
        <div className="flex flex-col" style={{ gap: 5 }}>
          {POST_MEETING_ACTION_ITEMS.map((item, index) => (
            <TeamsActionItem key={index} {...item} />
          ))}
        </div>
        <span
          className="text-blue-800 cursor-pointer"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400,
            fontSize: 7,
            lineHeight: '9px',
          }}
        >
          See all action items
        </span>
      </motion.div>

      {/* Next Meeting Section */}
      <motion.div
        className="flex flex-col border-b border-gray-200"
        style={{ paddingLeft: 7, paddingRight: 7, paddingTop: 9, paddingBottom: 9, gap: 9 }}
        variants={itemVariants}
      >
        <span
          className="text-gray-600"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400,
            fontSize: 7,
            lineHeight: '9px',
          }}
        >
          Next Meeting
        </span>
        <div className="flex items-center" style={{ gap: 5 }}>
          <TeamsCalendarIcon className="text-blue-800" style={{ width: 10, height: 10 }} />
          <span
            className="text-blue-800"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 400,
              fontSize: 7,
              lineHeight: '9px',
            }}
          >
            Jan 22, 2026 at 2:00 PM - Q4 Final Review
          </span>
        </div>
      </motion.div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Action Buttons Section */}
      <motion.div
        className="flex flex-col"
        style={{ paddingLeft: 7, paddingRight: 7, paddingTop: 9, paddingBottom: 9, gap: 7 }}
        variants={itemVariants}
      >
        {/* Primary Button - Draft Follow-up Email */}
        <button
          className="bg-blue-800 text-white flex items-center justify-center"
          style={{
            paddingTop: 5,
            paddingBottom: 5,
            paddingLeft: 8,
            paddingRight: 8,
            borderRadius: 5,
            gap: 3,
          }}
        >
          <TeamsMailIcon className="text-white" style={{ width: 10, height: 10 }} />
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 400,
              fontSize: 10,
              lineHeight: '15px',
            }}
          >
            Draft Follow-up Email
          </span>
        </button>

        {/* Outline Button - Export Notes */}
        <button
          className="bg-white border border-blue-1000 text-blue-1000 flex items-center justify-center"
          style={{
            paddingTop: 5,
            paddingBottom: 5,
            paddingLeft: 8,
            paddingRight: 8,
            borderRadius: 5,
            gap: 3,
          }}
        >
          <TeamsDownloadIcon className="text-blue-1000" style={{ width: 10, height: 10 }} />
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 400,
              fontSize: 10,
              lineHeight: '15px',
            }}
          >
            Export Notes
          </span>
        </button>

        {/* Outline Button - Share via Teams */}
        <button
          className="bg-white border border-blue-1000 text-blue-1000 flex items-center justify-center"
          style={{
            paddingTop: 5,
            paddingBottom: 5,
            paddingLeft: 8,
            paddingRight: 8,
            borderRadius: 5,
            gap: 3,
          }}
        >
          <TeamsShareIcon className="text-blue-1000" style={{ width: 10, height: 10 }} />
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 400,
              fontSize: 10,
              lineHeight: '15px',
            }}
          >
            Share via Teams
          </span>
        </button>
      </motion.div>
    </div>
  </motion.div>
);

// ==========================================
// TEAMS MEETINGS CARD - Main Component
// Shows two Teams sidebars: during meeting (left) and post-meeting (right)
// ==========================================
const TeamsMeetingsCard = () => (
  <motion.article
    className="relative w-full max-w-[520px] flex items-start justify-center gap-[12px] pt-[16px]"
    aria-label="Teams meetings showcase card"
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    {/* Left Sidebar - During Meeting */}
    <motion.div className="flex flex-col items-center" variants={itemVariants}>
      <TeamsSidebarMini />
    </motion.div>

    {/* Right Sidebar - Post Meeting */}
    <motion.div className="flex flex-col items-center" variants={itemVariants}>
      <TeamsPostMeetingSidebar />
    </motion.div>
  </motion.article>
);

// ==========================================
// MICROSOFT INTEGRATION CARD - Excel Mini Sidebar
// Exact match to Figma node 1065-2428
// Full size: 341px × 809px, Scale factor: ~58.6% → 200px width
// ==========================================

// Icon props type
type IconProps = {
  className?: string;
  style?: React.CSSProperties;
};

// Grid/Table icon for "Data Selected" row
const GridIcon = ({ className, style }: IconProps) => (
  <svg className={className} style={style} viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1.5" y="1.5" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1" />
    <line x1="1.5" y1="5" x2="11.5" y2="5" stroke="currentColor" strokeWidth="1" />
    <line x1="1.5" y1="8.5" x2="11.5" y2="8.5" stroke="currentColor" strokeWidth="1" />
    <line x1="5" y1="1.5" x2="5" y2="11.5" stroke="currentColor" strokeWidth="1" />
    <line x1="8.5" y1="1.5" x2="8.5" y2="11.5" stroke="currentColor" strokeWidth="1" />
  </svg>
);

// Sheet/Document icon for sheet row
const SheetIcon = ({ className, style }: IconProps) => (
  <svg className={className} style={style} viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7.5 1.5H3.5C2.94772 1.5 2.5 1.94772 2.5 2.5V10.5C2.5 11.0523 2.94772 11.5 3.5 11.5H9.5C10.0523 11.5 10.5 11.0523 10.5 10.5V4.5L7.5 1.5Z"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M7.5 1.5V4.5H10.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Chevron right icon for A1:D10
const ChevronRightSmallIcon = ({ className, style }: IconProps) => (
  <svg className={className} style={style} viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 3.5L8.5 6.5L5 9.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ExcelSidebarMini = () => (
  <motion.div
    className="relative"
    style={{ width: 250, height: 474 }} // Container width: green strip visible + white panel
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    {/* Green left strip - visible portion on the left side */}
    <div
      className="absolute bg-[#a0e3c4]"
      style={{
        left: 0,
        top: 0,
        width: 73,
        height: '100%',
        borderTopLeftRadius: 7,
        borderBottomLeftRadius: 7,
      }}
    />

    {/* Excel logo positioned in the green strip area */}
    <motion.div
      className="absolute"
      style={{ left: 14, top: 14, width: 25, height: 23 }}
      variants={itemVariants}
    >
      <ExcelIcon size={25} className="w-full h-full" />
    </motion.div>

    {/* White main panel with green border - offset to reveal green strip */}
    <div
      className="absolute bg-white border-2 border-[#a0e3c4] flex flex-col"
      style={{
        left: 50,
        top: 0,
        width: 200,
        height: '100%',
        borderRadius: 7,
      }}
    >
      {/* Archie "A." Logo pill - centered at top */}
      {/* Original: w-64 h-44 rounded-24 → scaled: w-37 h-26 rounded-14 */}
      <motion.div
        className="flex justify-center"
        style={{ paddingTop: 16, paddingBottom: 12 }}
        variants={itemVariants}
      >
        <div
          className="flex items-center justify-center"
          style={{
            width: 37,
            height: 26,
            borderRadius: 14,
          }}
        >
          <ALogoIcon size={36} className="text-blue-800" />
        </div>
      </motion.div>

      {/* Data Selected Card */}
      {/* Original: px-12 py-16 gap-16 rounded-8 → scaled: px-7 py-9 gap-9 rounded-5 */}
      <motion.div
        className="bg-white border border-gray-200 flex flex-col"
        style={{
          marginLeft: 11,
          marginRight: 11,
          paddingLeft: 7,
          paddingRight: 7,
          paddingTop: 9,
          paddingBottom: 9,
          gap: 9,
          borderRadius: 5,
        }}
        variants={itemVariants}
      >
        {/* Row 1: Data Selected + A1:D10 + chevron */}
        <div className="flex items-center justify-between">
          <div className="flex items-center" style={{ gap: 5, height: 9 }}>
            <GridIcon className="text-blue-1000" style={{ width: 8, height: 8 }} />
            <span
              className="text-blue-1000"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 400,
                fontSize: 7,
                lineHeight: '9px',
              }}
            >
              Data Selected
            </span>
          </div>
          <div className="flex items-center" style={{ gap: 5, height: 9 }}>
            <span
              className="text-blue-800"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 400,
                fontSize: 7,
                lineHeight: '9px',
              }}
            >
              A1:D10
            </span>
            <ChevronRightSmallIcon className="text-blue-800" style={{ width: 8, height: 8 }} />
          </div>
        </div>

        {/* Row 2: Sheet info + Change */}
        <div className="flex items-center justify-between">
          <div className="flex items-center" style={{ gap: 5, height: 9 }}>
            <SheetIcon className="text-gray-600" style={{ width: 8, height: 8 }} />
            <span
              className="text-gray-600"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 400,
                fontSize: 7,
                lineHeight: '9px',
              }}
            >
              Sheet 1, A1:D10
            </span>
          </div>
          <span
            className="text-blue-800"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 400,
              fontSize: 7,
              lineHeight: '9px',
            }}
          >
            Change
          </span>
        </div>

        {/* Row 3: Cell count (with hidden icon space for alignment) */}
        <div className="flex items-center" style={{ height: 9 }}>
          {/* Hidden icon placeholder for alignment */}
          <div style={{ width: 8, height: 8, opacity: 0, marginRight: 5 }} />
          <span
            className="text-gray-600"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 400,
              fontSize: 7,
              lineHeight: '9px',
            }}
          >
            40 cells, 4 columns
          </span>
        </div>
      </motion.div>

      {/* Spacer to push suggestions and input to bottom */}
      <div className="flex-1" />

      {/* Suggestions Section */}
      <motion.div
        className="flex flex-col"
        style={{
          marginLeft: 11,
          marginRight: 11,
          gap: 7.031,
        }}
        variants={itemVariants}
      >
        {/* Label: matches Figma 5.859px bold uppercase */}
        <span
          className="text-gray-600 uppercase"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 700,
            fontSize: 5.859,
            lineHeight: 'normal',
          }}
        >
          Suggestions
        </span>
        {/* Pills container: gap matches Figma 4.688px */}
        <div className="flex flex-wrap" style={{ gap: 4.688 }}>
          {EXCEL_SUGGESTIONS.map((suggestion, index) => (
            <div
              key={index}
              className="bg-gray-50 flex items-center"
              style={{
                paddingLeft: 7.031,
                paddingRight: 7.031,
                paddingTop: 2.344,
                paddingBottom: 2.344,
                borderRadius: 2.344,
                border: '0.586px solid var(--gray-200, #e5e7f0)',
              }}
            >
              {/* Pill text: matches Figma 7.031px */}
              <span
                className="text-blue-1000"
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 400,
                  fontSize: 7.031,
                  lineHeight: '9.375px',
                }}
              >
                {suggestion}
              </span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Chat Input */}
      <div style={{ marginLeft: 11, marginRight: 11, marginTop: 12, marginBottom: 12 }}>
        <ArchieChatInput size="md" placeholder="Ready to talk spreadsheets..." />
      </div>
    </div>
  </motion.div>
);

// ==========================================
// MICROSOFT INTEGRATION CARD - Main Component
// ==========================================
const MicrosoftIntegrationCard = () => (
  <motion.article
    className="relative w-full max-w-[520px] flex items-start justify-center gap-[12px] pt-[16px]"
    aria-label="Microsoft integration showcase card"
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    {/* Excel Sidebar - green strip and logo integrated per Figma design */}
    <motion.div className="flex flex-col items-center" variants={itemVariants}>
      <ExcelSidebarMini />
    </motion.div>

    {/* Teams Sidebar - purple strip and logo now integrated per Figma design */}
    <motion.div className="flex flex-col items-center" variants={itemVariants}>
      <TeamsSidebarMini />
    </motion.div>
  </motion.article>
);

// ==========================================
// BULK REGISTRATION CARD - Icons
// ==========================================
const AlertTriangleIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.134 1.866a1 1 0 0 1 1.732 0l4.134 7.134A1 1 0 0 1 10.134 10.5H1.866a1 1 0 0 1-.866-1.5l4.134-7.134Z"
      stroke="#8f6c1a"
      strokeWidth="0.8"
      fill="none"
    />
    <path d="M6 4v2.5" stroke="#8f6c1a" strokeWidth="0.8" strokeLinecap="round" />
    <circle cx="6" cy="8" r="0.5" fill="#8f6c1a" />
  </svg>
);

// ==========================================
// BULK REGISTRATION CARD - Data Constants
// ==========================================
const EXECUTION_LOG_DATA = [
  { company: 'Meridian Holdings LLC', status: 'complete' as const },
  { company: 'Apex Digital Services Inc', status: 'complete' as const },
  { company: 'TechFlow Solutions Corp', status: 'processing' as const },
  { company: 'Summit Consulting Group', status: 'queued' as const },
];

// ==========================================
// BULK REGISTRATION CARD - Status Badge Component
// ==========================================
type StatusBadgeProps = {
  status: 'complete' | 'processing' | 'queued';
};

const StatusBadge = ({ status }: StatusBadgeProps) => {
  const styles = {
    complete: 'bg-[rgba(113,212,129,0.08)] border-[#71d481] text-[#71d481]',
    processing: 'bg-[rgba(21,177,205,0.08)] border-blue-800 text-blue-800',
    queued: 'bg-gray-100 border-gray-200 text-gray-600',
  };

  const labels = {
    complete: 'Complete',
    processing: 'Processing',
    queued: 'Queued',
  };

  return (
    <div className={`flex items-center justify-center border rounded-[3px] px-[6px] py-[1.5px] ${styles[status]}`}>
      <span
        className="text-[7.4px] leading-none"
        style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
      >
        {labels[status]}
      </span>
    </div>
  );
};

// ==========================================
// BULK REGISTRATION CARD - Main Component
// Scale factor: ~0.744 (matching scaled version in Figma)
// ==========================================
const BulkRegistrationCard = () => (
  <motion.article
    className="bg-gray-50 rounded-[9px] p-[12px] w-full max-w-[488px] shadow-[0px_6px_6px_0px_rgba(0,0,0,0.04),0px_15px_18px_0px_rgba(0,0,0,0.08)]"
    aria-label="Bulk registration showcase card"
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    {/* Chat Container */}
    <div className="flex flex-col gap-[12px]">
      {/* User Message Bubble */}
      <motion.div className="flex flex-col" variants={itemVariants}>
        <div className="bg-white border border-[#e5e7f0] rounded-[12px] p-[12px] w-full shadow-[0px_1.5px_3px_-1.5px_rgba(0,0,0,0.08),0px_3px_6px_-1.5px_rgba(0,0,0,0.04)]">
          <div className="flex flex-col py-[3px]">
            <p
              className="text-[12px] leading-[18px] text-blue-1000"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
            >
              I need to register 4,127 business accounts before tomorrow's 5pm deadline. Can you handle this?
            </p>
          </div>
        </div>
      </motion.div>

      {/* AI Response Section */}
      <div className="flex flex-col gap-[6px]">
        {/* AI Response Text */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[6px] rounded-[12px]">
            <div className="flex flex-col py-[3px]">
              <p
                className="text-[12px] leading-[18px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                Processing all 4,127 accounts now. I'll complete this overnight.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Progress Card */}
        <motion.div
          className="bg-white border border-gray-200 rounded-[6px] p-[12px] flex flex-col gap-[9px]"
          variants={itemVariants}
        >
          {/* Header Row */}
          <div className="flex items-center justify-between">
            <p
              className="text-[15px] leading-[21px] text-blue-1000"
              style={{ fontFamily: "'Signifier', serif", fontWeight: 200 }}
            >
              4,127 Accounts Queued
            </p>
            <div className="flex items-center justify-center bg-[rgba(113,212,129,0.08)] border border-[#71d481] rounded-[3px] px-[9px] py-[3px]">
              <span
                className="text-[9px] leading-none text-[#71d481]"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                Active
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="bg-gray-200 rounded-full h-[6px] w-full overflow-hidden">
            <div className="bg-blue-800 h-full w-[68%] rounded-full" />
          </div>

          {/* Stats Row */}
          <div className="flex gap-[40px]">
            <div className="flex flex-col">
              <span
                className="text-[9px] leading-[12px] text-blue-1000 font-semibold"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                2,806
              </span>
              <span
                className="text-[9px] leading-[12px] text-gray-600"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                Complete
              </span>
            </div>
            <div className="flex flex-col">
              <span
                className="text-[9px] leading-[12px] text-blue-1000 font-semibold"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                1,289
              </span>
              <span
                className="text-[9px] leading-[12px] text-gray-600"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                Pending
              </span>
            </div>
            <div className="flex flex-col">
              <span
                className="text-[9px] leading-[12px] text-blue-1000 font-semibold"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                32
              </span>
              <span
                className="text-[9px] leading-[12px] text-gray-600"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                Errors
              </span>
            </div>
          </div>
        </motion.div>

        {/* Execution Log Section */}
        <motion.div
          className="bg-white border border-gray-200 rounded-[9px] p-[6px] flex flex-col gap-[6px]"
          variants={itemVariants}
        >
          <div className="p-[6px]">
            {/* Section Header */}
            <div className="flex items-center py-[3px] mb-[6px]">
              <p
                className="text-[7.4px] leading-normal text-gray-600 uppercase font-bold"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Execution log
              </p>
            </div>

            {/* Table */}
            <div className="border border-[rgba(0,0,0,0.1)] rounded-[1px] overflow-hidden">
              {/* Table Header */}
              <div className="bg-[rgba(236,238,242,0.3)] border-b border-[rgba(0,0,0,0.1)] px-[6px] py-[5px] flex items-center justify-between">
                <span
                  className="text-[9px] leading-[12px] text-[#0a0a0a] font-medium"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Company Name
                </span>
                <span
                  className="text-[9px] leading-[12px] text-[#0a0a0a] font-medium"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Status
                </span>
              </div>

              {/* Table Rows */}
              {EXECUTION_LOG_DATA.map((row, index) => (
                <div
                  key={index}
                  className={`px-[6px] py-[5px] flex items-center justify-between ${
                    index < EXECUTION_LOG_DATA.length - 1 ? 'border-b border-[rgba(0,0,0,0.1)]' : ''
                  }`}
                >
                  <span
                    className="text-[9px] leading-[12px] text-[#0a0a0a]"
                    style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
                  >
                    {row.company}
                  </span>
                  <StatusBadge status={row.status} />
                </div>
              ))}
            </div>

            {/* Warning Card */}
            <div
              className="bg-[rgba(242,229,40,0.08)] border border-[#8f6c1a] rounded-[6px] p-[6px]"
              style={{ marginTop: 12 }}
            >
              <div className="flex gap-[6px] items-start">
                <AlertTriangleIcon className="w-[9px] h-[9px] flex-shrink-0 mt-[2px]" />
                <div className="flex flex-col">
                  <span
                    className="text-[9px] leading-[12px] text-[#8f6c1a] font-medium"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    32 accounts flagged for review
                  </span>
                  <span
                    className="text-[9px] leading-[12px] text-[#717182]"
                    style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
                  >
                    Duplicate IDs detected • Manual verification queued
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </motion.article>
);

// ==========================================
// STATE TAX FILING CARD - Stat Card Component
// ==========================================
type StatCardProps = {
  value: string;
  label: string;
  variant: 'green' | 'blue' | 'gray';
};

const StatCard = ({ value, label, variant }: StatCardProps) => {
  const styles = {
    green: {
      bg: 'bg-[rgba(113,212,129,0.08)]',
      border: 'border-[#71d481]',
      text: 'text-[#71d481]',
    },
    blue: {
      bg: 'bg-[rgba(21,177,205,0.08)]',
      border: 'border-blue-800',
      text: 'text-blue-800',
    },
    gray: {
      bg: 'bg-gray-100',
      border: 'border-gray-200',
      text: 'text-[#595f7a]',
    },
  };

  const s = styles[variant];

  return (
    <div className={`flex-1 ${s.bg} border ${s.border} rounded-[6px] p-[12px] flex flex-col items-center justify-center gap-[3px]`}>
      <span
        className={`text-[14px] leading-[18px] font-semibold ${s.text}`}
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        {value}
      </span>
      <span
        className={`text-[9px] leading-[12px] ${s.text}`}
        style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
      >
        {label}
      </span>
    </div>
  );
};

// ==========================================
// STATE TAX FILING CARD - Progress Bar Component
// ==========================================
type ProgressBarProps = {
  percentage: number;
};

const FilingProgressBar = ({ percentage }: ProgressBarProps) => (
  <div className="bg-gray-200 rounded-full h-[6px] w-full overflow-hidden">
    <div
      className="bg-blue-800 h-full rounded-full transition-all duration-300"
      style={{ width: `${percentage}%` }}
    />
  </div>
);

// ==========================================
// STATE TAX FILING CARD - Section Heading (scaled for this card)
// ==========================================
const FilingSectionHeading = ({ children }: { children: React.ReactNode }) => (
  <p
    className="text-[15px] leading-[21px] text-blue-1000"
    style={{
      fontFamily: "'Signifier', serif",
      fontWeight: 200,
    }}
  >
    {children}
  </p>
);

// ==========================================
// STATE TAX FILING CARD - Main Component
// Scale factor: ~73.27% (matching Figma scaled version)
// ==========================================
const StateTaxFilingCard = () => (
  <motion.article
    className="bg-gray-50 rounded-[9px] p-[12px] w-full max-w-[488px] shadow-[0px_6px_6px_0px_rgba(0,0,0,0.04),0px_15px_18px_0px_rgba(0,0,0,0.08)]"
    aria-label="State tax filing showcase card"
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    {/* Chat Container */}
    <div className="flex flex-col gap-[12px]">
      {/* User Message Bubble */}
      <motion.div className="flex flex-col" variants={itemVariants}>
        <div className="bg-white border border-[#e5e7f0] rounded-[12px] p-[12px] w-full shadow-[0px_1.5px_3px_-1.5px_rgba(0,0,0,0.08),0px_3px_6px_-1.5px_rgba(0,0,0,0.04)]">
          <div className="flex flex-col py-[3px]">
            <p
              className="text-[12px] leading-[18px] text-blue-1000"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
            >
              I need to file state taxes in 30 states for Q4. Walk me through the status.
            </p>
          </div>
        </div>
      </motion.div>

      {/* AI Response Section */}
      <div className="flex flex-col gap-[6px]">
        {/* AI Response Text */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[6px] rounded-[12px]">
            <div className="flex flex-col py-[3px]">
              <p
                className="text-[12px] leading-[18px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                Processing all 4,127 accounts now. I'll complete this overnight.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Status Container */}
        <motion.div
          className="bg-white border border-gray-200 rounded-[9px] p-[12px] flex flex-col gap-[12px]"
          variants={itemVariants}
        >
          {/* Stat Cards Row */}
          <div className="flex gap-[12px]">
            <StatCard value="24" label="Filed" variant="green" />
            <StatCard value="6" label="In Progress" variant="blue" />
            <StatCard value="80%" label="Complete" variant="gray" />
          </div>

          {/* Progress Bar */}
          <FilingProgressBar percentage={80} />
        </motion.div>

        {/* Recently Filed Section */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[6px] rounded-[12px] flex flex-col gap-[6px]">
            <div className="flex flex-col py-[3px]">
              <FilingSectionHeading>Recently Filed</FilingSectionHeading>
            </div>
            <div className="flex flex-col py-[3px]">
              <ul
                className="list-disc pl-[14px] text-[12px] leading-[18px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <li>
                  <span style={{ fontWeight: 600 }}>California:</span>
                  <span style={{ fontWeight: 400 }}> $127,450</span>
                </li>
                <li>
                  <span style={{ fontWeight: 600 }}>New York:</span>
                  <span style={{ fontWeight: 400 }}> $98,320</span>
                </li>
                <li>
                  <span style={{ fontWeight: 600 }}>Pennsylvania:</span>
                  <span style={{ fontWeight: 400 }}> $71,200</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Currently Processing Section */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[6px] rounded-[12px] flex flex-col gap-[6px]">
            <div className="flex flex-col py-[3px]">
              <FilingSectionHeading>Currently Processing</FilingSectionHeading>
            </div>
            <div className="flex flex-col py-[3px]">
              <ul
                className="list-disc pl-[14px] text-[12px] leading-[18px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <li>
                  <span style={{ fontWeight: 600 }}>Texas:</span>
                  <span style={{ fontWeight: 400 }}> $82,100 </span>
                  <span style={{ fontWeight: 400, color: '#595f7a' }}>(validating)</span>
                </li>
                <li>
                  <span style={{ fontWeight: 600 }}>Florida:</span>
                  <span style={{ fontWeight: 400 }}> $71,890 </span>
                  <span style={{ fontWeight: 400, color: '#595f7a' }}>(submitting)</span>
                </li>
                <li>
                  <span style={{ fontWeight: 600 }}>Illinois:</span>
                  <span style={{ fontWeight: 400 }}> $64,220 </span>
                  <span style={{ fontWeight: 400, color: '#595f7a' }}>(preparing)</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Data Extraction Section */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[6px] rounded-[12px] flex flex-col gap-[6px]">
            <div className="flex flex-col py-[3px]">
              <FilingSectionHeading>Data Extraction</FilingSectionHeading>
            </div>
            <div className="flex flex-col py-[3px]">
              <ul
                className="list-disc pl-[14px] text-[12px] leading-[18px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                <li>Sales data pulled from GL exports</li>
                <li>Tax rates applied per state requirements</li>
                <li>Filing records maintained for audit trail</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </motion.article>
);

// ==========================================
// TAX GUIDANCE CARD - Icons
// ==========================================
const FileTextIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7 1H3C2.44772 1 2 1.44772 2 2V10C2 10.5523 2.44772 11 3 11H9C9.55228 11 10 10.5523 10 10V4L7 1Z"
      stroke="currentColor"
      strokeWidth="0.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7 1V4H10"
      stroke="currentColor"
      strokeWidth="0.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 6H8"
      stroke="currentColor"
      strokeWidth="0.8"
      strokeLinecap="round"
    />
    <path
      d="M4 8H6"
      stroke="currentColor"
      strokeWidth="0.8"
      strokeLinecap="round"
    />
  </svg>
);

const GlobeIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="0.8" />
    <ellipse cx="6" cy="6" rx="2" ry="5" stroke="currentColor" strokeWidth="0.8" />
    <path d="M1 6H11" stroke="currentColor" strokeWidth="0.8" />
  </svg>
);

// ==========================================
// TAX GUIDANCE CARD - Citation Badge Component
// ==========================================
type CitationBadgeProps = {
  number: string;
};

const CitationBadge = ({ number }: CitationBadgeProps) => (
  <div className="inline-flex items-center justify-center bg-gray-100 border border-gray-200 rounded-[8px] w-[13px] h-[13px] ml-[4px]">
    <span
      className="text-[8px] leading-[11px] text-gray-600 text-center"
      style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
    >
      {number}
    </span>
  </div>
);

// ==========================================
// TAX GUIDANCE CARD - Source Pill Component
// ==========================================
type SourcePillProps = {
  text: string;
};

const SourcePill = ({ text }: SourcePillProps) => (
  <div className="bg-gray-100 border border-gray-200 rounded-[8px] px-[3px] py-[1.5px] flex items-center gap-[3px]">
    <FileTextIcon className="w-[8px] h-[8px] text-gray-800" />
    <span
      className="text-[7px] leading-[11px] text-gray-800"
      style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
    >
      {text}
    </span>
  </div>
);

// ==========================================
// TAX GUIDANCE CARD - Main Component
// Scale factor: ~0.697 (matching ExcelAnalysisCard proportions)
// ==========================================
const TaxGuidanceCard = () => (
  <motion.article
    className="bg-gray-50 rounded-[8px] p-[11px] w-full max-w-[488px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.04),0px_10px_12px_0px_rgba(0,0,0,0.08)]"
    aria-label="Tax guidance showcase card"
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    {/* Chat Container */}
    <div className="flex flex-col gap-[11px]">
      {/* User Message Bubble */}
      <motion.div className="flex flex-col" variants={itemVariants}>
        <div className="bg-white border border-[#e5e7f0] rounded-[11px] p-[11px] w-full shadow-[0px_1.4px_2.8px_-1.4px_rgba(0,0,0,0.08),0px_2.8px_5.6px_-1.4px_rgba(0,0,0,0.04)]">
          <div className="flex flex-col gap-[5.5px] py-[2.8px]">
            <p
              className="text-[11px] leading-[17px] text-blue-1000"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
            >
              Hey, Archie. How does Section 179 apply to this CNC machining equipment we purchased for $385,000?
            </p>
          </div>
        </div>
      </motion.div>

      {/* AI Response Section */}
      <div className="flex flex-col">
        {/* Section 179 Deduction Eligibility Heading */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[5.5px] rounded-[11px]">
            <div className="flex flex-col py-[2.8px]">
              <SectionHeading>Section 179 Deduction Eligibility</SectionHeading>
            </div>
          </div>
        </motion.div>

        {/* Bullet List with Citation */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[5.5px] rounded-[11px] flex flex-col gap-[5.5px]">
            <div className="flex flex-col py-[2.8px]">
              <ul
                className="list-disc pl-[14px] text-[11px] leading-[17px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <li>
                  <span style={{ fontWeight: 600 }}>Qualified Property:</span>
                  <span style={{ fontWeight: 400 }}> CNC machining equipment qualifies as tangible personal property under IRC §179(d)</span>
                  <CitationBadge number="1" />
                </li>
                <li>
                  <span style={{ fontWeight: 600 }}>2025 Deduction Limit:</span>
                  <span style={{ fontWeight: 400 }}> Maximum $1,220,000 deduction available (2025 inflation-adjusted limit)</span>
                </li>
                <li>
                  <span style={{ fontWeight: 600 }}>Phase-Out Threshold:</span>
                  <span style={{ fontWeight: 400 }}> Phase-out begins at $3,050,000 total equipment purchases (verify total annual purchases)</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* State Considerations Section */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[5.5px] rounded-[11px] flex flex-col gap-[5.5px]">
            <div className="flex flex-col py-[2.8px]">
              <SectionHeading>State Considerations</SectionHeading>
            </div>
            <div className="flex flex-col py-[2.8px]">
              <p
                className="text-[11px] leading-[17px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                Some states decouple from federal §179 limits. Verify state conformity for addback requirements.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Sources Section */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="px-[5.5px] rounded-[11px]">
            <div className="flex flex-wrap items-center gap-[5.5px] py-[2.8px]">
              <p
                className="text-[11px] leading-[17px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                Sources:
              </p>
              <SourcePill text="IRC §179(b) - Dollar Limitation" />
              <SourcePill text="IRC §179(d) - Section 179 Property Definition" />
              <SourcePill text="Rev. Proc. 2024-40 - 2025 Inflation Adjustments" />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </motion.article>
);

// ==========================================
// BOOKKEEPING GUIDANCE CARD - Web Source Pill Component
// ==========================================
type WebSourcePillProps = {
  text: string;
};

const WebSourcePill = ({ text }: WebSourcePillProps) => (
  <div className="bg-gray-100 border border-gray-200 rounded-[8px] px-[3px] py-[1.5px] flex items-center gap-[3px]">
    <GlobeIcon className="w-[8px] h-[8px] text-gray-800" />
    <span
      className="text-[7px] leading-[11px] text-gray-800"
      style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
    >
      {text}
    </span>
  </div>
);

// ==========================================
// BOOKKEEPING GUIDANCE CARD - Main Component
// Scale factor: ~0.722 (matching Figma scaled version)
// ==========================================
const BookkeepingGuidanceCard = () => (
  <motion.article
    className="bg-gray-50 rounded-[8px] p-[11px] w-full max-w-[488px] shadow-[0px_5.8px_5.8px_0px_rgba(0,0,0,0.04),0px_14.4px_17.3px_0px_rgba(0,0,0,0.08)]"
    aria-label="Bookkeeping guidance showcase card"
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    {/* Chat Container */}
    <div className="flex flex-col gap-[11px]">
      {/* User Message Bubble */}
      <motion.div className="flex flex-col" variants={itemVariants}>
        <div className="bg-white border border-[#e5e7f0] rounded-[11px] p-[11px] w-full shadow-[0px_1.4px_2.9px_-1.4px_rgba(0,0,0,0.08),0px_2.9px_5.8px_-1.4px_rgba(0,0,0,0.04)]">
          <div className="flex flex-col py-[2.9px]">
            <p
              className="text-[11px] leading-[17px] text-blue-1000"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
            >
              How do I record a customer deposit for a job that won't complete until next quarter?
            </p>
          </div>
        </div>
      </motion.div>

      {/* AI Response Section */}
      <div className="flex flex-col">
        {/* Section Heading */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[5.8px] rounded-[11px]">
            <div className="flex flex-col py-[2.9px]">
              <p
                className="text-[14.4px] leading-[20px] text-blue-1000"
                style={{
                  fontFamily: "'Signifier', serif",
                  fontWeight: 200,
                }}
              >
                Recording Customer Deposits
              </p>
            </div>
          </div>
        </motion.div>

        {/* Bulleted Instructions List */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[5.8px] rounded-[11px] flex flex-col gap-[5.8px]">
            <div className="flex flex-col py-[2.9px]">
              <ul
                className="list-disc pl-[17px] text-[11px] leading-[17px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <li className="mb-[2px]">
                  <span style={{ fontWeight: 600 }}>Create a Customer Deposit liability account:</span>
                  <br />
                  <span style={{ fontWeight: 400 }}>Chart of Accounts → New → Other Current Liabilities → "Customer Deposits"</span>
                </li>
                <li className="mb-[2px]">
                  <span style={{ fontWeight: 600 }}>Record the deposit receipt:</span>
                  <br />
                  <span style={{ fontWeight: 400 }}>Banking → Make Deposits → Select "Customer Deposits" account (not income)</span>
                </li>
                <li className="mb-[2px]">
                  <span style={{ fontWeight: 600 }}>Link deposit to customer record:</span>
                  <br />
                  <span style={{ fontWeight: 400 }}>Use "Received From" field → Enter customer name → Add memo: "Deposit for [Project Name]"</span>
                </li>
                <li className="mb-[2px]">
                  <span style={{ fontWeight: 600 }}>Apply deposit when invoicing (next quarter):</span>
                  <br />
                  <span style={{ fontWeight: 400 }}>Create Invoice → Apply Credits → Select customer deposit → Apply to final invoice</span>
                </li>
                <li>
                  <span style={{ fontWeight: 600 }}>Verify liability account clears:</span>
                  <br />
                  <span style={{ fontWeight: 400 }}>Balance Sheet → Customer Deposits should show $0 after final invoice application</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Important Callout Box */}
        <motion.div className="flex flex-col px-[5.8px]" variants={itemVariants}>
          <div className="bg-gray-100 border border-gray-200 rounded-[4.3px] p-[5.8px]">
            <div className="flex flex-col py-[2.9px]">
              <p
                className="text-[10px] leading-[14px] text-gray-800"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                Important
              </p>
              <p
                className="text-[8.7px] leading-[11.5px] text-gray-600"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                Do not record as revenue until work is performed. Customer deposits are a liability until earned.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Sources Section */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="px-[5.8px] rounded-[11px]">
            <div className="flex flex-wrap items-center gap-[5.8px] py-[2.9px]">
              <p
                className="text-[11px] leading-[17px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                Sources:
              </p>
              <WebSourcePill text="QuickBooks: How to Handle Customer Deposits" />
              <SourcePill text="IRC §179(d) - Section 179 Property Definition" />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </motion.article>
);

// ==========================================
// TECHNICAL ACCOUNTING CARD - Main Component
// Scale factor: ~0.697 (matching ExcelAnalysisCard proportions)
// ==========================================
const TechnicalAccountingCard = () => (
  <motion.article
    className="bg-gray-50 rounded-[8px] p-[11px] w-full max-w-[488px] shadow-[0px_5.6px_5.6px_0px_rgba(0,0,0,0.04),0px_14px_16.8px_0px_rgba(0,0,0,0.08)]"
    aria-label="Technical accounting answers showcase card"
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    {/* Chat Container - gap-11px (scaled from 16px) */}
    <div className="flex flex-col gap-[11px]">
      {/* User Message Bubble */}
      <motion.div className="flex flex-col" variants={itemVariants}>
        {/* Bubble: p-11px, rounded-11px (scaled from p-16, rounded-16) */}
        <div className="bg-white border border-[#e5e7f0] rounded-[11px] p-[11px] w-full shadow-[0px_1.4px_2.8px_-1.4px_rgba(0,0,0,0.08),0px_2.8px_5.6px_-1.4px_rgba(0,0,0,0.04)]">
          {/* Text container - py-2.8px (scaled from py-4) */}
          <div className="flex flex-col py-[2.8px]">
            {/* Message text - 11px/17px (scaled from 16px/24px) */}
            <p
              className="text-[11px] leading-[17px] text-blue-1000"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
            >
              When do we recognize a right-of-use asset under ASC 842 for our new office lease?
            </p>
          </div>
        </div>
      </motion.div>

      {/* Assistant Response Container */}
      <div className="flex flex-col">
        {/* Intro text bubble - p-5.5px (scaled from p-8) */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[5.5px] rounded-[11px]">
            <div className="flex flex-col py-[2.8px]">
              {/* Body text - 11px/17px */}
              <p
                className="text-[11px] leading-[17px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                Under ASC 842, you must recognize the ROU asset at lease commencement. Here's what you need to know:
              </p>
            </div>
          </div>
        </motion.div>

        {/* Recognition Timing Section */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[5.5px] rounded-[11px] flex flex-col gap-[5.5px]">
            <div className="flex flex-col py-[2.8px]">
              <SectionHeading>Recognition Timing</SectionHeading>
            </div>
            <div className="flex flex-col py-[2.8px]">
              <p
                className="text-[11px] leading-[17px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                Recognize on the commencement date - the date the lessor makes the underlying asset available for use (ASC 842-10-55-6). This is typically when you take possession, not when the lease is signed.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Initial Measurement Section */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[5.5px] rounded-[11px] flex flex-col gap-[5.5px]">
            <div className="flex flex-col py-[2.8px]">
              <SectionHeading>Initial Measurement</SectionHeading>
            </div>
            <div className="flex flex-col py-[2.8px]">
              <p
                className="text-[11px] leading-[17px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                ROU Asset = Lease Liability + Initial Direct Costs + Prepayments - Lease Incentives Received
              </p>
            </div>
          </div>
        </motion.div>

        {/* Operating vs. Finance Lease Section */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[5.5px] rounded-[11px] flex flex-col gap-[5.5px]">
            <div className="flex flex-col py-[2.8px]">
              <SectionHeading>Operating vs. Finance Lease</SectionHeading>
            </div>
            <div className="flex flex-col py-[2.8px]">
              <p
                className="text-[11px] leading-[17px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                Classify as finance lease if: ownership transfers, purchase option likely, lease term ≥75% of economic life, PV ≥90% of fair value, or no alternative use. Otherwise, operating lease.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Short-Term Exemption Section */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="p-[5.5px] rounded-[11px] flex flex-col gap-[5.5px]">
            <div className="flex flex-col py-[2.8px]">
              <SectionHeading>Short-Term Exemption</SectionHeading>
            </div>
            <div className="flex flex-col py-[2.8px]">
              <p
                className="text-[11px] leading-[17px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                You may elect not to recognize ROU assets for leases ≤12 months. Expense these on a straight-line basis instead (ASC 842-20-25-2).
              </p>
            </div>
          </div>
        </motion.div>

        {/* Sources Section */}
        <motion.div className="flex flex-col" variants={itemVariants}>
          <div className="px-[5.5px] rounded-[11px]">
            <div className="flex flex-wrap items-center gap-[5.5px] py-[2.8px]">
              <p
                className="text-[11px] leading-[17px] text-blue-1000"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                Sources:
              </p>
              <SourcePill text="ASC 842-10-25 - Recognition" />
              <SourcePill text="ASC 842-20-30 - Initial Measurement (Lessee)" />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </motion.article>
);

// ==========================================
// VARIANCE ANALYSIS CARD - Arrow Icons
// ==========================================
const ArrowUpIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 8 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 1.5V6.5M4 1.5L1.5 4M4 1.5L6.5 4"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowDownIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 8 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 6.5V1.5M4 6.5L1.5 4M4 6.5L6.5 4"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ==========================================
// VARIANCE ANALYSIS CARD - Variance Badge Component
// ==========================================
type VarianceBadgeProps = {
  value: string;
  isPositive: boolean;
};

const VarianceBadge = ({ value, isPositive }: VarianceBadgeProps) => (
  <div className="flex items-center gap-[2px]">
    {isPositive ? (
      <ArrowUpIcon className="w-[8.8px] h-[8.8px] text-[#595f7a]" />
    ) : (
      <ArrowDownIcon className="w-[8.8px] h-[8.8px] text-[#595f7a]" />
    )}
    <span
      className="text-[8.8px] leading-[11.7px] text-[#595f7a]"
      style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500 }}
    >
      {value}
    </span>
  </div>
);

// ==========================================
// VARIANCE ANALYSIS CARD - Main Component
// Scale factor: ~0.73 (matching Figma scaled version)
// ==========================================
const VarianceAnalysisCard = () => (
  <motion.article
    className="bg-gray-50 rounded-[8.8px] p-[11.7px] w-full max-w-[488px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.04),0px_10px_12px_0px_rgba(0,0,0,0.08)]"
    aria-label="Variance analysis showcase card"
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    {/* Chat Container */}
    <div className="flex flex-col gap-[11.7px]">
      {/* User Message Bubble */}
      <motion.div className="flex flex-col" variants={itemVariants}>
        <div className="bg-white border border-[#e5e7f0] rounded-[11.7px] p-[11.7px] w-full shadow-[0px_1.4px_2.8px_-1.4px_rgba(0,0,0,0.08),0px_2.8px_5.6px_-1.4px_rgba(0,0,0,0.04)]">
          <div className="flex flex-col py-[2.9px]">
            <p
              className="text-[11.7px] leading-[17.5px] text-blue-1000"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
            >
              Analyze Q4 performance vs. budget and explain the key drivers.
            </p>
          </div>
        </div>
      </motion.div>

      {/* AI Response Container */}
      <div className="flex flex-col">
        <div className="flex flex-col gap-[5.8px] p-[5.8px] rounded-[11.7px]">
          {/* Title */}
          <motion.div className="flex flex-col py-[2.9px]" variants={itemVariants}>
            <p
              className="text-[17.5px] leading-[23.4px] text-blue-1000"
              style={{
                fontFamily: "'Signifier', serif",
                fontWeight: 200,
              }}
            >
              Here's Your Q4 Variance Analysis:
            </p>
          </motion.div>

        {/* Variance Table */}
        <motion.div className="flex flex-col py-[8.8px]" variants={itemVariants}>
          {/* Table Header */}
          <div
            className="grid grid-cols-4 gap-[5.8px] px-[5.8px] py-[5.8px] border border-[#e5e7f0] -mb-px"
            style={{ backgroundColor: 'rgba(236,238,242,0.5)' }}
          >
            <span
              className="text-[8.8px] leading-[11.7px] text-blue-1000 text-left"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600 }}
            >
              Account
            </span>
            <span
              className="text-[8.8px] leading-[11.7px] text-blue-1000 text-right"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600 }}
            >
              Actual
            </span>
            <span
              className="text-[8.8px] leading-[11.7px] text-blue-1000 text-right"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600 }}
            >
              Budget
            </span>
            <span
              className="text-[8.8px] leading-[11.7px] text-blue-1000 text-right"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600 }}
            >
              Var
            </span>
          </div>
          {/* Table Rows */}
          <div className="border border-[#e5e7f0]">
            {[
              { account: 'Revenue', actual: '$2,847K', budget: '$2,650K', variance: '+7.4%', isPositive: true },
              { account: 'COGS', actual: '$1,124K', budget: '$1,060K', variance: '-6.0%', isPositive: false },
              { account: 'Gross Profit', actual: '$1,723K', budget: '$1,590K', variance: '+8.4%', isPositive: true },
              { account: 'OpEx', actual: '$982K', budget: '$920K', variance: '-6.7%', isPositive: false },
            ].map((row, index) => (
              <div
                key={row.account}
                className={`grid grid-cols-4 gap-[5.8px] px-[5.8px] py-[4.4px] ${
                  index < 3 ? 'border-b border-[rgba(0,0,0,0.1)]' : ''
                }`}
              >
                <span
                  className="text-[8.8px] leading-[11.7px] text-blue-1000 text-left"
                  style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
                >
                  {row.account}
                </span>
                <span
                  className="text-[8.8px] leading-[11.7px] text-blue-1000 text-right"
                  style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
                >
                  {row.actual}
                </span>
                <span
                  className="text-[8.8px] leading-[11.7px] text-[#595f7a] text-right"
                  style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
                >
                  {row.budget}
                </span>
                <div className="flex justify-end">
                  <VarianceBadge value={row.variance} isPositive={row.isPositive} />
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Revenue Beat Section */}
        <motion.div className="flex flex-col gap-[5.8px]" variants={itemVariants}>
          <p
            className="text-[14.6px] leading-[20.5px] text-blue-1000"
            style={{
              fontFamily: "'Signifier', serif",
              fontWeight: 200,
            }}
          >
            Revenue Beat (+$197K)
          </p>
          <ul
            className="list-disc pl-[14px] text-[11.7px] leading-[17.5px] text-blue-1000"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
          >
            <li>Enterprise segment: +$142K (3 new logos)</li>
            <li>Expansion revenue: +$68K (upsells)</li>
            <li>Lower churn: -$13K retained</li>
          </ul>
        </motion.div>

        {/* OpEx Overspend Section */}
        <motion.div className="flex flex-col gap-[5.8px]" variants={itemVariants}>
          <p
            className="text-[14.6px] leading-[20.5px] text-blue-1000"
            style={{
              fontFamily: "'Signifier', serif",
              fontWeight: 200,
            }}
          >
            OpEx Overspend (+$62K)
          </p>
          <ul
            className="list-disc pl-[14px] text-[11.7px] leading-[17.5px] text-blue-1000"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
          >
            <li>Headcount additions: +$41K salary/benefits</li>
            <li>Marketing campaign: +$18K digital spend</li>
            <li>Travel normalization: +$9K</li>
          </ul>
        </motion.div>

        {/* Key Metrics Section */}
        <motion.div className="flex flex-col gap-[5.8px]" variants={itemVariants}>
          <p
            className="text-[14.6px] leading-[20.5px] text-blue-1000"
            style={{
              fontFamily: "'Signifier', serif",
              fontWeight: 200,
            }}
          >
            Key Metrics
          </p>
          <p
            className="text-[11.7px] leading-[17.5px] text-blue-1000"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
          >
            Gross margin: 60.5% (+0.6pp) · Operating margin: 26.0% (+0.4pp)
            <br />
            YoY revenue growth: +32.1% · Operating leverage improving
          </p>
        </motion.div>
        </div>
      </div>
    </div>
  </motion.article>
);

// ==========================================
// MAIN CARD RENDERER
// ==========================================
type ShowcaseCardProps = {
  cardType: CapabilityCardType;
};

export const ShowcaseCard = ({ cardType }: ShowcaseCardProps) => {
  switch (cardType) {
    case 'excel-analysis':
      return <ExcelAnalysisCard />;
    case 'excel-sidebar':
      return <ExcelSidebarCard />;
    case 'microsoft-integration':
      return <MicrosoftIntegrationCard />;
    case 'teams-meetings':
      return <TeamsMeetingsCard />;
    case 'bulk-registration':
      return <BulkRegistrationCard />;
    case 'state-tax-filing':
      return <StateTaxFilingCard />;
    case 'tax-guidance':
      return <TaxGuidanceCard />;
    case 'bookkeeping-guidance':
      return <BookkeepingGuidanceCard />;
    case 'technical-accounting':
      return <TechnicalAccountingCard />;
    case 'variance-analysis':
      return <VarianceAnalysisCard />;
    default:
      return (
        <div className="bg-gray-50 rounded-2xl p-8 shadow-lg w-full">
          <p className="text-red-500">Unknown card type: {cardType}</p>
        </div>
      );
  }
};
